import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'COMPLETED', 'FAILED', 'REFUNDED'];
const LIMIT = 15;

export const UserPayments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [payments, setPayments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState('');
  const [payModal, setPayModal] = useState<any>(null);
  const [payMethod, setPayMethod] = useState('MOBILE_MONEY');
  const [payRef, setPayRef] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      const res = await apiClient.listPayments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setPayments(d?.payments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // User submits payment details — updates method/reference but does NOT complete.
  // Payment stays PENDING until admin approves (completes) it.
  const submitPaymentDetails = async () => {
    if (!payModal) return;
    setActionId(payModal.id);
    try {
      await apiClient.submitPaymentDetails(payModal.id, {
        paymentMethod: payMethod,
        transactionReference: payRef || undefined,
        notes: `Customer submitted via ${payMethod}${payRef ? ` — ref: ${payRef}` : ''}`,
      });
      setSubmitted(payModal.id);
      setPayModal(null);
      setPayRef('');
      toast('Payment details submitted. Awaiting admin approval.', 'success');
      load();
    } catch (err: any) {
      toast(err?.response?.data?.error || 'Failed to submit payment details', 'error');
    } finally { setActionId(''); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">My Payments</h1>
      </div>

      {payModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 420, margin: 0 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Submit Payment Details</h2>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#64748b' }}>Amount Due</div>
              <div style={{ fontWeight: 700, fontSize: 20 }}>{Number(payModal.amount).toLocaleString()} {payModal.currency}</div>
            </div>
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#0c4a6e' }}>
              ℹ️ Submit your payment details below. Your payment will be confirmed once reviewed and approved by our team.
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Payment Method</label>
              <select value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                {['MOBILE_MONEY', 'CASH', 'BANK_TRANSFER', 'CARD'].map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Transaction Reference <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <input type="text" value={payRef} onChange={e => setPayRef(e.target.value)} placeholder="e.g. MoMo transaction ID" />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={actionId === payModal.id} onClick={submitPaymentDetails}>
                {actionId === payModal.id ? 'Submitting...' : 'Submit Payment'}
              </button>
              <button className="btn btn-ghost" onClick={() => { setPayModal(null); setPayRef(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ marginBottom: 16 }}>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 180 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
        </div>

        {loading ? <div className="loading-spinner">Loading payments...</div> : payments.length === 0 ? (
          <div className="empty-state"><h3>No payments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Shipment</th><th>Amount</th><th>Method</th><th>Status</th><th>Due Date</th><th>Paid At</th><th></th></tr>
                </thead>
                <tbody>
                  {payments.map((p: any) => (
                    <tr key={p.id}>
                      <td><code style={{ fontSize: 11 }}>{p.shipment?.trackingNumber ?? p.shipmentId}</code></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{Number(p.amount).toLocaleString()} {p.currency}</td>
                      <td>{p.paymentMethod}</td>
                      <td>
                        <span className={`badge badge-${p.status?.toLowerCase()}`}>{p.status}</span>
                        {p.status === 'COMPLETED' && <span style={{ fontSize: 11, color: '#16a34a', marginLeft: 6 }}>✓ Approved</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>{p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '—'}</td>
                      <td style={{ fontSize: 12 }}>{p.paidAt ? new Date(p.paidAt).toLocaleDateString() : '—'}</td>
                      <td>
                        {p.status === 'PENDING' && submitted !== p.id && (
                          <button className="btn btn-primary" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => setPayModal(p)}>Pay Now</button>
                        )}
                        {(p.status === 'PENDING' && submitted === p.id) && (
                          <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 500 }}>⏳ Awaiting approval</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
